/**
 * PATH: packages/ui/src/kanban/components/card-detail/CardFilesRail.tsx
 * FILE_ID: UI-KANBAN-CMP-CARD-FILES-RAIL-001
 * PURPOSE: Surface the card's email attachments as compact, one-click
 *          file chips — a horizontally-scrolling RAIL at the top of the
 *          Card tab (so files are reachable without digging into the
 *          Files tab) and a vertical PANEL the Files tab itself reuses.
 *          ARC-2: PDF/image chips OPEN IN-CARD via the host-injected
 *          document editor overlay; panel chips expose the version rail.
 * WHY:     Operator 2026-06-23 (screenshot): "files should be here on
 *          the top rail with scroll if too many files"; operator
 *          2026-07-02 (ARC-2 / U11): "PDFs and images and such should
 *          always open inside … reusing … PDF editors … versioned where
 *          I can go to previous versions."
 * CONSTRAINT: @quotech/ui CANNOT import email-hub (dependency cycle).
 *          Everything — download, editor overlay, version endpoints —
 *          rides the tenant's `KanbanEmailAdapter`. The chip visuals
 *          live in CardFileChip.tsx (operator 300-line ceiling).
 * FLOW:
 *   ┌────────────┐   ┌──────────────────┐   ┌─────────────────────────────┐
 *   │ card linked│ → │ adapter.getEmail │ → │ chips → open in-card editor │
 *   │ email ids  │   │ (.attachments)   │   │ (adapter overlay) or        │
 *   │            │   │ (shared rq cache)│   │ downloadAttachment()        │
 *   └────────────┘   └──────────────────┘   └─────────────────────────────┘
 * RELATED:
 *   - card-detail/CardLinkedEmailSection.tsx: shares the EXACT react-query
 *     key (`['kanban','card-linked-email', id]`) so the email is fetched once.
 *   - CardFileChip.tsx: chip visuals + classification (extracted).
 *   - FileVersionRail.tsx: per-file version history (panel variant).
 *   - api/adapters-email.ts: KanbanEmailAdapter.DocumentEditorOverlay seam.
 */
import { useMemo, useState, type ReactNode } from 'react'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { Paperclip } from 'lucide-react'

import { cn } from '../../../lib/utils'
import type { KanbanEmailAdapter, KanbanEmailAttachment } from '../../api'
import type { Card } from '../../types'
import { cardCommentsQueryKey } from '../peekQueryKeys'
import { cardAttachmentsQueryKey } from './CardAttachmentsSection'
import { CardFileChip, styleFor } from './CardFileChip'
import { FileVersionRail, fileVersionsQueryKey } from './FileVersionRail'
import { orderLinkedEmailIds } from './linkedEmails'

/** Cards may include attachments allocated from related emails too, but the
 *  detail email is the source of real, downloadable file metadata. Inline
 *  cid: signature/body images are dropped (matches the Email Hub's
 *  `isUserVisibleAttachment`). */
function userVisibleFiles(
  list: ReadonlyArray<KanbanEmailAttachment> | undefined,
): KanbanEmailAttachment[] {
  return (list ?? []).filter((a) => a.is_inline !== true)
}

/** One aggregated row: the attachment + WHICH email it came from (the
 *  editor overlay needs the anchor email for reply / save flows). */
interface FileRow {
  att: KanbanEmailAttachment
  emailId: string
}

export interface CardFilesRailProps {
  card: Card
  emailAdapter?: KanbanEmailAdapter
  /** 'rail' = horizontal scroll strip (top of Card tab); 'panel' = wrap
   *  grid (the Files tab reuses this). */
  variant?: 'rail' | 'panel'
  /** Only the 'panel' variant renders an empty state (the Files tab owns
   *  the whole pane). The 'rail' stays invisible when there are no files. */
  emptyState?: boolean
  /** Rail-only: jump to the full Files tab (e.g. when downloads aren't wired). */
  onOpenFiles?: () => void
}

export function CardFilesRail({
  card,
  emailAdapter,
  variant = 'rail',
  emptyState = false,
  onOpenFiles,
}: CardFilesRailProps) {
  const qc = useQueryClient()
  const [busyId, setBusyId] = useState<string | number | null>(null)
  const [errorId, setErrorId] = useState<string | number | null>(null)
  const [openDoc, setOpenDoc] = useState<FileRow | null>(null)
  const [historyFor, setHistoryFor] = useState<string | null>(null)

  // BUG-2026-06-29-KANBAN-FILES-PRIMARY-ONLY — the rail used to fetch only
  // `card.primary_email`, so attachments on the card's RELATED emails never
  // appeared (the "3 files but card shows one" complaint). Aggregate across
  // EVERY linked email. We reuse the SAME ['kanban','card-linked-email', id]
  // query key the LinkedEmailRow / Email-tab chips warm, so on the Card tab
  // these are already cached — no extra network for the aggregation.
  const linkedEmailIds = useMemo(
    () => orderLinkedEmailIds(card.primary_email, card.related_emails),
    [card.primary_email, card.related_emails],
  )

  const emailQs = useQueries({
    queries: linkedEmailIds.map((id) => ({
      queryKey: ['kanban', 'card-linked-email', id, card.id],
      queryFn: () => emailAdapter!.getEmail(id, { cardId: card.id }),
      enabled: !!emailAdapter,
      staleTime: 30_000,
    })),
  })

  // Flatten every linked email's attachments, de-duplicated by id (so the
  // same file linked twice isn't listed twice), keeping the source email id
  // alongside. Cheap to recompute each render — small cached payloads.
  const files: FileRow[] = []
  {
    const seen = new Set<string>()
    emailQs.forEach((q, i) => {
      for (const att of userVisibleFiles(q.data?.attachments)) {
        const key = String(att.id)
        if (seen.has(key)) continue
        seen.add(key)
        files.push({ att, emailId: linkedEmailIds[i] })
      }
    })
  }

  const anyLoading = emailQs.some((q) => q.isLoading)
  const hintCount =
    card.attachment_count ?? card.related_attachment_ids_json?.length ?? 0

  const Overlay = emailAdapter?.DocumentEditorOverlay

  /** ARC-2 — a chip opens in-card when the host injected the overlay and
   *  the attachment is a document kind the editors handle (pdf/image). */
  function opensInCard(att: KanbanEmailAttachment): boolean {
    if (!Overlay) return false
    const kind = styleFor(att).meta.kind
    return kind === 'pdf' || kind === 'image'
  }

  async function download(att: KanbanEmailAttachment) {
    if (!(emailAdapter?.downloadAttachment && att.download_url)) {
      onOpenFiles?.()
      return
    }
    setBusyId(att.id)
    setErrorId(null)
    try {
      await emailAdapter.downloadAttachment(att, { cardId: card.id })
    } catch {
      setErrorId(att.id)
    } finally {
      setBusyId(null)
    }
  }

  function onPrimary(row: FileRow) {
    if (opensInCard(row.att)) {
      setOpenDoc(row)
      return
    }
    void download(row.att)
  }

  const renderChip = (row: FileRow) => {
    const { att } = row
    const canDownload = !!(emailAdapter?.downloadAttachment && att.download_url)
    const showHistory =
      variant === 'panel' &&
      !!att.qdrive_file_id &&
      !!emailAdapter?.listFileVersions
    const historyOpen = historyFor === String(att.id)
    return (
      <div
        key={String(att.id)}
        className={cn(variant === 'rail' ? 'shrink-0' : 'w-full space-y-1')}
      >
        <CardFileChip
          att={att}
          variant={variant}
          busy={busyId === att.id}
          failed={errorId === att.id}
          onPrimary={() => onPrimary(row)}
          opensEditor={opensInCard(att)}
          onDownload={canDownload ? () => void download(att) : undefined}
          onToggleHistory={
            showHistory
              ? () => setHistoryFor(historyOpen ? null : String(att.id))
              : undefined
          }
          historyOpen={historyOpen}
        />
        {historyOpen && att.qdrive_file_id && emailAdapter && (
          <FileVersionRail
            fileId={att.qdrive_file_id}
            emailAdapter={emailAdapter}
          />
        )}
      </div>
    )
  }

  // The host overlay portal — rendered regardless of list state so an
  // already-open editor survives a background refetch.
  const overlayNode = openDoc && Overlay && (
    <Overlay
      attachment={openDoc.att}
      emailId={openDoc.emailId}
      cardId={card.id}
      onClose={() => setOpenDoc(null)}
      onSaved={() => {
        // A markup save wrote a new version — refresh that file's rail.
        const fid = openDoc.att.qdrive_file_id
        if (fid) void qc.invalidateQueries({ queryKey: fileVersionsQueryKey(fid) })
      }}
      onFollowUpCreated={() => {
        // ARC-2/U12 — a follow-up landed as a comment + PNG attachments:
        // refresh both feeds so they show without a reopen.
        void qc.invalidateQueries({ queryKey: cardCommentsQueryKey(card.id) })
        void qc.invalidateQueries({ queryKey: cardAttachmentsQueryKey(card.id) })
      }}
    />
  )

  // Nothing to show and no way to fetch → render nothing.
  if (linkedEmailIds.length === 0 || !emailAdapter) {
    return variant === 'panel' && emptyState ? <FilesEmpty /> : null
  }

  // First load — reserve the rail's height only if the card hints files
  // exist, so the strip doesn't pop in and shove the body down. Only show
  // the skeleton while we have NO files yet (so files from an
  // already-resolved email aren't hidden behind a slower sibling fetch).
  if (anyLoading && files.length === 0) {
    if (variant === 'rail' && hintCount === 0) return null
    return (
      <Section variant={variant}>
        <div
          className={cn(
            variant === 'rail'
              ? 'flex gap-2 overflow-hidden'
              : 'grid grid-cols-1 gap-2 sm:grid-cols-2',
          )}
        >
          {Array.from({ length: Math.min(hintCount || 2, 4) }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-[42px] animate-pulse rounded-lg bg-neutral-100 dark:bg-zinc-800',
                variant === 'rail' ? 'w-[180px] shrink-0' : 'w-full',
              )}
            />
          ))}
        </div>
      </Section>
    )
  }

  if (files.length === 0) {
    return variant === 'panel' && emptyState ? <FilesEmpty /> : null
  }

  return (
    <>
      <Section variant={variant} count={files.length} onOpenFiles={onOpenFiles}>
        {variant === 'rail' ? (
          <div
            role="list"
            className="flex gap-2 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:thin]"
          >
            {files.map(renderChip)}
          </div>
        ) : (
          <div role="list" className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {files.map(renderChip)}
          </div>
        )}
      </Section>
      {overlayNode}
    </>
  )
}

function Section({
  variant,
  count,
  onOpenFiles,
  children,
}: {
  variant: 'rail' | 'panel'
  count?: number
  onOpenFiles?: () => void
  children: ReactNode
}) {
  return (
    <section aria-label="Files">
      <div className="mb-1 flex items-center justify-between">
        <h3 className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 dark:text-neutral-400">
          <Paperclip className="h-3 w-3" aria-hidden />
          Files{count ? ` · ${count}` : ''}
        </h3>
        {variant === 'rail' && onOpenFiles && (
          <button
            type="button"
            onClick={onOpenFiles}
            className="text-[11px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            All files →
          </button>
        )}
      </div>
      {children}
    </section>
  )
}

function FilesEmpty() {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-neutral-500 dark:text-zinc-400">
      <Paperclip className="h-8 w-8 opacity-40" aria-hidden />
      <p className="text-sm font-medium">No files on this card yet</p>
      <p className="max-w-[280px] text-[11.5px] leading-snug">
        {/* BUG-2026-06-29-KANBAN-FILES-EMPTY-COPY — the old copy promised
            "drag a file onto the card", but there's no upload drop handler
            here; files come from the linked emails' attachments. */}
        Attachments from this card&apos;s linked emails show up here
        automatically. Link an email on the Card tab to bring its files in.
      </p>
    </div>
  )
}
